import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { LocalstorageService } from '@services/localstorage.service';
import { environment } from 'environments/environment';
import { DEFAULT_ROUTE, LOCALSTORAGE } from './core/constants/constants';
import { authGuard } from './core/guards/auth.guard';
import { Roles } from './features/interface/auth';

const suffix = ` - Sbkash ${!environment.prod ? '(' + environment.env + ')' : ''}`;

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./shared/components/layout/layout/layout.component').then((c) => c.LayoutComponent),
    canMatch: [authGuard],
    data: {
      roles: [Roles.CLIENT, Roles.SUPER_ADMIN, Roles.DEVICE_USER],
    },
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/components/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent,
          ),
        title: 'Dashboard' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.CLIENT, Roles.SUPER_ADMIN, Roles.DEVICE_USER],
        },
      },
      {
        path: 'payments',
        loadComponent: () =>
          import('./features/components/payments/payments-list/payments-list.component').then(
            (m) => m.PaymentsListComponent,
          ),
        title: 'Payments' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.CLIENT, Roles.SUPER_ADMIN, Roles.DEVICE_USER],
        },
      },
      {
        path: 'clients',
        loadComponent: () =>
          import('./features/components/clients/clients-list/clients-list.component').then(
            (m) => m.ClientsListComponent,
          ),
        title: 'Clients' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.SUPER_ADMIN],
        },
      },
      {
        path: 'device-users',
        loadComponent: () =>
          import(
            './features/components/device-users/device-users-list/device-users-list.component'
          ).then((m) => m.DeviceUsersListComponent),
        title: 'Device Users' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.CLIENT, Roles.SUPER_ADMIN],
        },
      },
      {
        path: 'device-users/:clientId',
        loadComponent: () =>
          import('./features/components/device-users/device-payment/device-payment.component').then(
            (m) => m.DevicePaymentComponent,
          ),
        title: 'Device Users Payment' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.CLIENT, Roles.SUPER_ADMIN],
        },
      },
      {
        path: 'domain-users',
        loadComponent: () =>
          import(
            './features/components/domain-users/domain-users-list/domain-users-list.component'
          ).then((m) => m.DomainUsersListComponent),
        title: 'Domain Users' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.CLIENT],
        },
      },
      {
        path: 'api-user',
        loadComponent: () =>
          import('./features/components/api-user/api-user-list/api-user-list.component').then(
            (m) => m.ApiUserListComponent,
          ),
        title: 'API User' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.CLIENT, Roles.DEVICE_USER],
        },
      },
      {
        path: 'domain-users/:clientId',
        loadComponent: () =>
          import('./features/components/domain-users/domain-payment/domain-payment.component').then(
            (m) => m.DomainPaymentComponent,
          ),
        title: 'Domain Payment' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.CLIENT],
        },
      },
      {
        path: 'stored-payments',
        loadComponent: () =>
          import(
            './features/components/stored-payments/stored-payments-list/stored-payments-list.component'
          ).then((m) => m.StoredPaymentsListComponent),
        title: 'Stored Payments' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.SUPER_ADMIN, Roles.CLIENT, Roles.DEVICE_USER],
        },
      },
      {
        path: 'void-payments',
        loadComponent: () =>
          import(
            './features/components/stored-payments/void-payment-list/void-payment-list.component'
          ).then((m) => m.VoidPaymentListComponent),
        title: 'Void Payments' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.SUPER_ADMIN, Roles.CLIENT, Roles.DEVICE_USER],
        },
      },
      {
        path: 'blocked-payments',
        loadComponent: () =>
          import(
            './features/components/stored-payments/blocked-payments/blocked-payments.component'
          ).then((m) => m.BlockedPaymentsComponent),
        title: 'Blocked Payments' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.SUPER_ADMIN],
        },
      },
      {
        path: 'fraud-logs',
        loadComponent: () =>
          import('./features/components/fraud-logs/fraud-logs.component').then(
            (m) => m.FraudLogsComponent,
          ),
        title: 'Fraud Logs' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.SUPER_ADMIN],
        },
      },
      {
        path: 'link-telegram',
        loadComponent: () =>
          import('./features/components/link-telegram/link-telegram.component').then(
            (m) => m.LinkTelegramComponent,
          ),
        title: 'Link Telegram' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.CLIENT, Roles.DEVICE_USER],
        },
      },
      {
        path: 'withdrawal-requests',
        loadComponent: () =>
          import(
            './features/components/withdrawal-requests/payment-withdrawal-request/payment-withdrawal-request.component'
          ).then((m) => m.PaymentWithdrawalRequestComponent),
        title: 'Withdrawal Requests' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.CLIENT, Roles.DEVICE_USER],
        },
      },
      {
        path: 'all-withdrawal-requests',
        loadComponent: () =>
          import(
            './features/components/withdrawal-requests/payment-withdrawal-request/payment-withdrawal-request.component'
          ).then((m) => m.PaymentWithdrawalRequestComponent),
        title: 'All Withdrawal Requests' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.CLIENT, Roles.DEVICE_USER],
          hideActions: true,
        },
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./features/components/admin-settings/admin-settings.component').then(
            (m) => m.AdminSettingsComponent,
          ),
        title: 'Settings' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.CLIENT],
          hideActions: true,
        },
      },
      // {
      //   path: 'select-senders-account',
      //   loadComponent: () =>
      //     import(
      //       './features/components/sender-account-listing/sender-account-listing.component'
      //     ).then((m) => m.SenderAccountListingComponent),
      //   title: 'Stored Payments' + suffix,
      //   canMatch: [authGuard],
      //   data: {
      //     roles: [Roles.DEVICE_USER],
      //   },
      // },
      {
        path: 'mobile-bank',
        loadComponent: () =>
          import(
            './features/components/mobile-bank-settings/mobile-bank-form/mobile-bank-form.component'
          ).then((m) => m.MobileBankFormComponent),
        title: 'Mobile Bank' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.CLIENT, Roles.SUPER_ADMIN, Roles.DEVICE_USER],
        },
      },

      {
        path: 'brand-settings',
        loadComponent: () =>
          import('./features/components/brand-settings/api-key/api-key.component').then(
            (m) => m.ApiKeyComponent,
          ),
        title: 'Brand Settings' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.CLIENT],
        },
      },
      {
        path: 'activity-logs',
        loadComponent: () =>
          import(
            './features/components/activity-logs/activity-logs-list/activity-logs-list.component'
          ).then((m) => m.ActivityLogsListComponent),
        title: 'Activity Logs' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.CLIENT, Roles.SUPER_ADMIN, Roles.DEVICE_USER],
        },
      },
      {
        path: 'download-app',
        loadComponent: () =>
          import('./features/components/download-app/download-app.component').then(
            (m) => m.DownloadAppComponent,
          ),
        title: 'Download App' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.DEVICE_USER, Roles.CLIENT],
        },
      },
      {
        path: 'complaints',
        loadComponent: () =>
          import('./features/components/complaints/complaints-list/complaints-list.component').then(
            (m) => m.ComplaintsListComponent,
          ),
        title: 'Complaints' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.SUPER_ADMIN, Roles.CLIENT, Roles.DEVICE_USER],
        },
      },
      {
        path: 'authenticator',
        loadComponent: () =>
          import(
            './features/components/authenticator/authenticator-list/authenticator-list.component'
          ).then((m) => m.AuthenticatorListComponent),
        title: 'Authenticator' + suffix,
        canMatch: [authGuard],
        data: {
          roles: [Roles.CLIENT],
        },
      },
    ],
  },
  {
    path: 'auth',
    loadComponent: () =>
      import('./features/components/auth/auth-wrapper/auth-wrapper.component').then(
        (m) => m.AuthWrapperComponent,
      ),
    children: [
      { path: '', redirectTo: 'login', pathMatch: 'full' },
      {
        path: 'login',
        loadComponent: () =>
          import('./features/components/auth/login/login.component').then((m) => m.LoginComponent),
        title: 'Login' + suffix,
      },
      {
        path: '**',
        redirectTo: 'login',
      },
    ],
  },

  {
    path: '**',
    redirectTo: () => {
      const localStorageService = inject(LocalstorageService);
      const token = localStorageService.getItem(LOCALSTORAGE.TOKEN);
      return token ? DEFAULT_ROUTE : '/auth';
    },
  },
];
